import React, { useState, useEffect } from 'react';
import { PostSessions } from '../../service/SessionAPI';
import { Toast } from './Toast';

export const EditSession = ({ onClose, empId, sessionData, slot, fetchSessions }) => {
  // Prefill fields from props
  const [sessionDate, setSessionDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [registrationsAllowed, setRegistrationsAllowed] = useState('');

  // For enabling/disabling update button
  const [isUpdateEnabled, setIsUpdateEnabled] = useState(false);
  const registeredCount = slot?.registered_count || 0;

  // For field validation
  const [errors, setErrors] = useState({
    sessionDate: '',
    startTime: '',
    endTime: '',
    registrationsAllowed: ''
  });
 
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
 
  // Function to get quarter date range
  const getQuarterDateRange = (quarter, year = new Date().getFullYear()) => {
    switch (quarter) {
      case '1':
        return { min: `${year}-04-01`, max: `${year}-06-30` }; // April to June
      case '2':
        return { min: `${year}-07-01`, max: `${year}-09-30` }; // July to September
      case '3':
        return { min: `${year}-10-01`, max: `${year}-12-31` }; // October to December
      case '4':
        return { min: `${year + 1}-01-01`, max: `${year +1 }-03-31` }; // January to March
      default:
        return { min: getTodayDate(), max: getTodayDate() };
    }
  };
 
  // Helper: Strip AM/PM from time string
  const stripAmPm = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.replace(/\s?(AM|PM)$/i, '');
  };

  // Prefill form fields on mount
  useEffect(() => {
    if (slot) {
      setSessionDate(slot.session_date ? slot.session_date.split('T')[0] : '');
      setStartTime(stripAmPm(slot.start_time));
      setEndTime(stripAmPm(slot.end_time));
      setRegistrationsAllowed(slot.seat_available ? String(slot.seat_available) : '');
    }
  }, [slot]);

  // Enable update button only if any field is changed and all valid
  useEffect(() => {
    const isChanged =
      sessionDate !== (slot?.session_date ? slot.session_date.split('T')[0] : '') ||
      startTime !== stripAmPm(slot?.start_time) ||
      endTime !== stripAmPm(slot?.end_time) ||
      registrationsAllowed !== String(slot?.seat_available || '');

    const isValid =
      !errors.sessionDate &&
      !errors.startTime &&
      !errors.endTime &&
      !errors.registrationsAllowed &&
      sessionDate &&
      startTime &&
      endTime &&
      registrationsAllowed;

    setIsUpdateEnabled(isChanged && isValid);
  }, [sessionDate, startTime, endTime, registrationsAllowed, errors, slot]);

  // Validation handlers
  const validateFields = (field, value) => {
    let errorMsg = '';
    switch (field) {
      case 'sessionDate':
        if (!value) errorMsg = 'Enter session date';
        break;
      case 'startTime':
        if (!value) errorMsg = 'Enter start time';
        break;
      case 'endTime':
        if (!value) errorMsg = 'Enter end time';
        else if (startTime && value <= startTime) errorMsg = 'End time must be after start time';
        break;
      case 'registrationsAllowed':
        if (!value) errorMsg = 'Enter registrations allowed count';
        else if (!/^\d+$/.test(value) || parseInt(value) < 1) errorMsg = 'Must be a positive number (min 1)';
        else if (parseInt(value) < registeredCount) errorMsg = `Cannot reduce seats below registered count (${registeredCount})`;
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [field]: errorMsg }));
  };

  // Handlers
  const handleSessionDateChange = (e) => {
    setSessionDate(e.target.value);
    validateFields('sessionDate', e.target.value);
  };
  const handleStartTimeChange = (e) => {
    setStartTime(e.target.value);
    validateFields('startTime', e.target.value);
    validateFields('endTime', endTime); // re-validate endTime
  };
  const handleEndTimeChange = (e) => {
    setEndTime(e.target.value);
    validateFields('endTime', e.target.value);
  };
  const handleRegistrationsAllowedChange = (e) => {
    setRegistrationsAllowed(e.target.value);
    validateFields('registrationsAllowed', e.target.value);
  };

  // Update API call
  const handleUpdate = async () => {
    if (!isUpdateEnabled) return;

    const payload = {
      session_id: sessionData.session_id,
      quarter: sessionData.quarter,
      session_name: sessionData.session_name,
      session_count: sessionData.session_count,
      grade:sessionData.grade,
      session_details: [
        {
          slot_id: slot.slot_id,
          session_date: sessionDate,
          start_time: startTime,
          end_time: endTime,
          seat_available: parseInt(registrationsAllowed),
          status: "Update"
        }
      ],
      empId: empId
    };

    try {
      const response = await PostSessions(payload);
      if (response.data.code === 200) {
        Toast.success("Slot Updated Successfully!");
        fetchSessions && fetchSessions();
        onClose();
      } else {
        Toast.error("Failed to update session");
      }
    } catch (error) {
      Toast.error("Failed to update session");
    }
  };
 
   const { min: minDate, max: maxDate } = getQuarterDateRange(sessionData.quarter);
 
  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" aria-labelledby="staticBackdropLabel" aria-modal="true" role="dialog">
      <div className="modal-dialog modal-md sessions-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title" id="staticBackdropLabel">Edit Session</h6>
            <button type="button" className="btn-close font-12" onClick={onClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="row">
              {/* Static fields */}
              <div className="col-md-6 mb-4">
                <label className="font-12 mb-1 regular-text grey-text d-block">Session Name</label>
                <label className="font-12 medium-text">{sessionData.session_name}</label>
              </div>
              <div className="col-md-6 mb-4">
                <label className="font-12 mb-1 regular-text grey-text d-block">Quarter</label>
                <label className="font-12 medium-text">{`Quarter ${sessionData.quarter}`}</label>
              </div>
              {/* Editable fields */}
              <div className="col-md-6 mb-4">
                <label className="font-12 mb-1 regular-text grey-text d-block" htmlFor="sessionDate">
                  Session Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control hive-form-control font-12 ${errors.sessionDate ? 'is-invalid' : ''}`}
                  id="sessionDate"
                  value={sessionDate}
                  onChange={handleSessionDateChange}
                  min={minDate}
                  max={maxDate}
                />
                {errors.sessionDate && <span className="text-danger font-12">{errors.sessionDate}</span>}
              </div>
              <div className="col-md-12 mb-4">
                <div className="row">
                  <div className="col-md-6">
                    <label className="font-12 mb-1 regular-text grey-text d-block" htmlFor="startTime">
                      Start Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className={`form-control hive-form-control font-12 ${errors.startTime ? 'is-invalid' : ''}`}
                      id="startTime"
                      value={startTime}
                      onChange={handleStartTimeChange}
                    />
                    {errors.startTime && <span className="text-danger font-12">{errors.startTime}</span>}
                  </div>
                  <div className="col-md-6">
                    <label className="font-12 mb-1 regular-text grey-text d-block" htmlFor="endTime">
                      End Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className={`form-control hive-form-control font-12 ${errors.endTime ? 'is-invalid' : ''}`}
                      id="endTime"
                      value={endTime}
                      onChange={handleEndTimeChange}
                    />
                    {errors.endTime && <span className="text-danger font-12">{errors.endTime}</span>}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <label className="font-12 mb-1 regular-text grey-text d-block" htmlFor="registrationsAllowed">
                  Registrations Allowed <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control hive-form-control font-12 ${errors.registrationsAllowed ? 'is-invalid' : ''}`}
                  id="registrationsAllowed"
                  value={registrationsAllowed}
                  onChange={handleRegistrationsAllowedChange}
                  placeholder="Enter value"
                />
                {errors.registrationsAllowed && <span className="text-danger font-12">{errors.registrationsAllowed}</span>}
              </div>
            </div>
          </div>
          <div className="modal-footer border-0">
            <div className="col-md-12 d-flex justify-content-end">
              <button className="btn hive-btn-secondary me-3" onClick={onClose}>Cancel</button>
              <button className="btn hive-btn-primary" disabled={!isUpdateEnabled} onClick={handleUpdate}>Update</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
